import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, Ban, Building2, CalendarClock, Check, ChevronDown, Clock3,
  Loader2, Package, PauseCircle, Plus, Radio, RotateCcw, Search, Sparkles, Target,
  Ticket as TicketIcon, UserRound, Zap,
} from "lucide-react";
import { toast } from "sonner";

import api from "@/lib/api";
import { unwrapCompaniesPage } from "@/lib/companies";
import { formatDate } from "@/lib/utils-ros";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PRIORITY_LIST = ["low", "medium", "high", "critical"];
const PRIORITY_COLOR = {
  critical: "var(--danger)", high: "var(--copper)",
  medium: "var(--warning)", low: "var(--info)",
};

const SOURCE_META = {
  ticket: { icon: TicketIcon, label: "Ticket" },
  meeting: { icon: CalendarClock, label: "Meeting" },
  opp_action: { icon: Target, label: "Opportunity" },
  lead_action: { icon: Zap, label: "Lead" },
  signal_action: { icon: Radio, label: "Signal" },
  universe_action: { icon: Building2, label: "Universe" },
  order_action: { icon: Package, label: "Order" },
  commitment: { icon: Check, label: "Commitment" },
  ai: { icon: Sparkles, label: "AI" },
  reminder: { icon: Clock3, label: "Reminder" },
  renewal: { icon: Clock3, label: "Renewal" },
  opportunity_park_reminder: { icon: PauseCircle, label: "Parked deal" },
  expansion: { icon: Target, label: "Land & Expand" },
  manual: { icon: Check, label: "Manual" },
};

const BUCKETS = [
  { k: "today", label: "Today", icon: CalendarClock },
  { k: "overdue", label: "Overdue", icon: AlertTriangle },
  { k: "upcoming", label: "Upcoming", icon: Clock3 },
  { k: "blocked", label: "Blocked", icon: Ban },
  { k: "waiting", label: "Waiting", icon: Clock3 },
  { k: "completed", label: "Completed", icon: Check },
];

const ENTITY_GROUPS = ["Universe", "Signals", "Leads", "Opportunities", "Orders"];
const EMPTY_TASK = { title: "", due: undefined, priority: "medium", entityKey: "" };
const TASK_ACTION_CLASS = "h-8 px-3 rounded-lg bg-[#E5F3F9] border border-[#BDD8E4] text-[#07539A] text-[10px] font-medium shadow-[0_3px_8px_rgba(7,83,154,0.08)] hover:bg-[#07539A] hover:text-[#FFFFFF] hover:border-[#07539A] hover:-translate-y-0.5 transition-all inline-flex items-center gap-1.5";

export default function Tasks() {
  const [buckets, setBuckets] = useState({});
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("today");
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [entityOptions, setEntityOptions] = useState([]);
  const [entityOptionsLoaded, setEntityOptionsLoaded] = useState(false);
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const [nt, setNt] = useState(EMPTY_TASK);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/tasks/buckets");
      setBuckets(data.buckets || {});
      setCounts(data.counts || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load tasks");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!adding || entityOptionsLoaded || entitiesLoading) return;

    async function loadEntityOptions() {
      setEntitiesLoading(true);
      const results = await Promise.allSettled([
        api.get("/companies", { params: { limit: 200 } }), api.get("/signals?limit=500"),
        api.get("/leads"), api.get("/opportunities"), api.get("/orders"),
      ]);
      const rows = results.map(r => r.status === "fulfilled" ? (r.value.data || []) : []);
      const companies = unwrapCompaniesPage(rows[0]).items;
      const [signals, leads, opportunities, orders] = rows.slice(1);
      const options = [
        ...companies.map(c => ({
          key: `company:${c.id}`, group: "Universe", label: c.name,
          detail: c.industry || "Customer", icon: Building2,
          payload: { company_id: c.id, source_type: "universe_action", source_id: c.id },
        })),
        ...signals.map(s => ({
          key: `signal:${s.id}`, group: "Signals", label: s.company?.name || s.intent || "Signal",
          detail: s.intent || s.signal_type || "Signal", icon: Radio,
          payload: { company_id: s.company_id, signal_id: s.id, source_type: "signal_action", source_id: s.id },
        })),
        ...leads.map(l => ({
          key: `lead:${l.id}`, group: "Leads", label: l.company?.name || l.contact_name || "Lead",
          detail: l.contact_name ? `${l.contact_name} · ${l.status}` : l.status || "Lead", icon: Zap,
          payload: { company_id: l.company_id, lead_id: l.id, source_type: "lead_action", source_id: l.id },
        })),
        ...opportunities.map(o => ({
          key: `opportunity:${o.id}`, group: "Opportunities", label: o.company?.name || o.name || "Opportunity",
          detail: o.name || o.current_gate?.replaceAll("_", " ") || "Opportunity", icon: Target,
          payload: { company_id: o.company_id, opportunity_id: o.id, source_type: "opp_action", source_id: o.id },
        })),
        ...orders.map(o => ({
          key: `order:${o.id}`, group: "Orders", label: o.company?.name || o.po_number || "Order",
          detail: o.po_number ? `PO ${o.po_number}` : "Order", icon: Package,
          payload: { company_id: o.company_id, opportunity_id: o.opportunity_id, source_type: "order_action", source_id: o.id },
        })),
      ];
      setEntityOptions(options);
      setEntityOptionsLoaded(true);
      setEntitiesLoading(false);
    }
    loadEntityOptions();
  }, [adding, entitiesLoading, entityOptionsLoaded]);

  async function update(id, patch) {
    try {
      await api.patch(`/tasks/${id}`, patch);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update task");
      throw err;
    }
  }

  async function complete(id) {
    await update(id, { status: "completed" });
    toast.success("Task completed");
  }

  function bucketFor(dueDate) {
    if (!dueDate) return "upcoming";
    const dt = new Date(dueDate);
    const now = new Date();
    if (dt < now) return "overdue";
    if (dt - now < 86400000) return "today";
    return "upcoming";
  }

  function closeTaskDialog() {
    setAdding(false);
    setNt(EMPTY_TASK);
  }

  async function createTask(e) {
    e?.preventDefault();
    const title = nt.title.trim();
    if (!title) { toast.error("Task title required"); return; }

    const selectedEntity = entityOptions.find(o => o.key === nt.entityKey);
    const payload = {
      title, priority: nt.priority, task_type: "reminder", source_type: "manual",
      ...(selectedEntity?.payload || {}),
    };
    if (nt.due) {
      const due = new Date(nt.due);
      due.setHours(23, 59, 59, 999);
      payload.due_date = due.toISOString();
    }

    setSaving(true);
    try {
      await api.post("/tasks", payload);
      toast.success("Task added");
      closeTaskDialog();
      setFilter(bucketFor(payload.due_date));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add task");
    } finally { setSaving(false); }
  }

  const shown = buckets[filter] || [];

  return (
    <div className="min-h-full bg-[#F7FAFC] pb-12" data-testid="tasks-page">
      <section className="bg-[#247DA8] px-5 md:px-9 py-5 md:py-6 flex items-center justify-between gap-4 shadow-[inset_0_-1px_0_rgba(255,255,255,0.18)]">
        <div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight text-[#FFFFFF]">To-Do</h1>
        </div>
        <button type="button" onClick={() => setAdding(true)} data-testid="add-task-btn" aria-label="Add task"
          className="w-12 h-12 md:w-14 md:h-14 rounded-full border-2 border-[#FFFFFF] text-[#FFFFFF] flex items-center justify-center hover:bg-[#FFFFFF] hover:text-[#07539A] hover:shadow-[0_8px_20px_rgba(0,0,0,0.18)] transition-all">
          <Plus size={28} strokeWidth={1.8} />
        </button>
      </section>

      <nav className="relative z-10 mx-3 md:mx-8 -mt-3 md:-mt-4 bg-white rounded-full border border-[#DCE8EE] shadow-[0_9px_24px_rgba(7,83,154,0.12)] p-1.5 flex items-center gap-1 overflow-x-auto" aria-label="Task buckets">
        {BUCKETS.map(b => {
          const active = filter === b.k;
          const n = counts[b.k] || 0;
          return (
            <button key={b.k} type="button" onClick={() => setFilter(b.k)} data-testid={`tasks-filter-${b.k}`}
              className={`min-w-max flex-1 h-10 px-3 md:px-5 rounded-full flex items-center justify-center gap-2 text-[12px] md:text-[13px] font-medium transition-all ${active ? "bg-[var(--copper)] text-[#FFFFFF] shadow-[0_5px_12px_rgba(214,138,46,0.26)]" : "text-[#243B53] hover:bg-[#EDF7FB] hover:text-[#07539A]"}`}>
              <b.icon size={14} strokeWidth={1.7} />
              <span>{b.label}</span>
              <span className={`font-mono text-[10px] tabular-nums ${active ? "text-[#FFFFFF]" : "text-[#78909C]"}`}>{n}</span>
            </button>
          );
        })}
      </nav>

      <main className="px-3 md:px-8 pt-6">
        {loading ? (
          <div className="min-h-44 rounded-[22px] border border-[#DCE8EE] bg-white flex items-center justify-center text-[#607D8B] shadow-[0_8px_24px_rgba(7,83,154,0.06)]">
            <Loader2 size={18} className="animate-spin mr-2" /> Loading tasks…
          </div>
        ) : shown.length === 0 ? (
          <div className="min-h-52 rounded-[22px] border border-dashed border-[#BFD5E0] bg-white/80 flex flex-col items-center justify-center text-center px-6 shadow-[0_8px_24px_rgba(7,83,154,0.04)]">
            <span className="w-11 h-11 rounded-full bg-[#E7F5FB] text-[#07539A] flex items-center justify-center mb-3"><Check size={20} /></span>
            <div className="font-display text-lg text-[#102A43]">Nothing here</div>
            <p className="text-[12px] text-[#78909C] mt-1">Enjoy the calm, or add a task with the plus button.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="task-list">
            {shown.map(t => <TaskCard key={t.id} t={t} filter={filter} onComplete={() => complete(t.id)} onUpdate={(patch) => update(t.id, patch)} />)}
          </div>
        )}
      </main>

      <AddTaskDialog open={adding} onOpenChange={(open) => { if (open) setAdding(true); else closeTaskDialog(); }} nt={nt} setNt={setNt}
        saving={saving} options={entityOptions} optionsLoading={entitiesLoading} onSubmit={createTask} />
    </div>
  );
}

function AddTaskDialog({ open, onOpenChange, nt, setNt, saving, options, optionsLoading, onSubmit }) {
  const selected = options.find(o => o.key === nt.entityKey);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl rounded-[26px] border border-[#BFD9E5] bg-[#EFF8FC] p-6 md:p-8 shadow-[0_28px_70px_rgba(7,83,154,0.22)]">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#07539A]">Add task</DialogTitle>
          <DialogDescription className="text-[#607D8B]">Create a commitment and connect it to the right customer record.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} data-testid="add-task-form" className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6 items-end">
          <div className="md:col-span-6">
            <label htmlFor="new-task-title" className="block text-[12px] font-semibold text-[#07539A] mb-1.5">Task</label>
            <input id="new-task-title" autoFocus type="text" value={nt.title} onChange={(e) => setNt(s => ({ ...s, title: e.target.value }))}
              placeholder="What needs to be done?" data-testid="add-task-title"
              className="w-full h-11 px-3.5 rounded-xl bg-white border border-[#B8D3DF] shadow-[0_4px_12px_rgba(7,83,154,0.06)] outline-none focus:border-[#00A2D4] focus:ring-2 focus:ring-[#00A2D4]/10 text-[14px]" />
          </div>
          <div className="md:col-span-6">
            <label className="block text-[12px] font-semibold text-[#07539A] mb-1.5">Customer</label>
            <CustomerPicker options={options} loading={optionsLoading} selected={selected} onSelect={(entityKey) => setNt(s => ({ ...s, entityKey }))} />
          </div>
          <div className="md:col-span-5">
            <label className="block text-[12px] font-semibold text-[#07539A] mb-1.5">Deadline</label>
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" data-testid="add-task-due" className="w-full h-11 px-3.5 rounded-xl bg-white border border-[#B8D3DF] shadow-[0_4px_12px_rgba(7,83,154,0.06)] hover:border-[#00A2D4] flex items-center gap-2 text-left">
                  <CalendarClock size={16} className="text-[#00A2D4]" />
                  <span className={nt.due ? "text-[#102A43]" : "text-[#78909C]"}>{nt.due ? formatDate(nt.due.toISOString()) : "Choose a deadline"}</span>
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-auto rounded-xl border-[#BFD9E5] p-0 shadow-xl">
                <Calendar mode="single" selected={nt.due} onSelect={(due) => setNt(s => ({ ...s, due }))} initialFocus />
              </PopoverContent>
            </Popover>
          </div>
          <div className="md:col-span-4">
            <label className="block text-[12px] font-semibold text-[#07539A] mb-1.5">Priority</label>
            <Select value={nt.priority} onValueChange={(priority) => setNt(s => ({ ...s, priority }))}>
              <SelectTrigger data-testid="add-task-priority" className="h-11 rounded-xl bg-white border-[#B8D3DF] shadow-[0_4px_12px_rgba(7,83,154,0.06)] focus:ring-[#00A2D4] capitalize"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-xl border-[#BFD9E5]">{PRIORITY_LIST.map(priority => <SelectItem key={priority} value={priority} className="capitalize">{priority}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3 flex md:justify-end">
            <button type="submit" disabled={saving} data-testid="add-task-save"
              className="w-full md:w-auto h-11 px-6 rounded-xl bg-[#07539A] text-[#FFFFFF] font-medium shadow-[0_8px_18px_rgba(7,83,154,0.22)] hover:bg-[#063F73] hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:translate-y-0 inline-flex items-center justify-center gap-2">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Add
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CustomerPicker({ options, loading, selected, onSelect }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => `${o.label} ${o.detail} ${o.group}`.toLowerCase().includes(q));
  }, [options, query]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="add-task-customer"
          className="w-full h-11 px-3.5 rounded-xl bg-white border border-[#B8D3DF] shadow-[0_4px_12px_rgba(7,83,154,0.06)] hover:border-[#00A2D4] flex items-center justify-between gap-3 text-left">
          <span className="min-w-0">
            <span className={`block truncate ${selected ? "text-[#102A43]" : "text-[#78909C]"}`}>{selected?.label || "Choose a customer record"}</span>
            {selected && <span className="block text-[10px] text-[#78909C] truncate">{selected.group} · {selected.detail}</span>}
          </span>
          {loading ? <Loader2 size={15} className="animate-spin text-[#00A2D4]" /> : <ChevronDown size={16} className="text-[#00A2D4] shrink-0" />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(92vw,34rem)] rounded-xl border-[#BFD9E5] p-2 shadow-[0_18px_45px_rgba(7,83,154,0.18)]">
        <div className="h-10 rounded-lg bg-[#F4FAFD] border border-[#D5E7EF] px-3 flex items-center gap-2">
          <Search size={14} className="text-[#00A2D4]" />
          <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} data-testid="customer-search" placeholder="Search customers and records…" className="flex-1 min-w-0 bg-transparent outline-none text-[13px]" />
        </div>
        <div className="mt-2 max-h-72 overflow-y-auto">
          <button type="button" onClick={() => { onSelect(""); setOpen(false); setQuery(""); }} data-testid="customer-none"
            className="w-full px-3 py-2 rounded-lg text-left text-[12px] text-[#607D8B] hover:bg-[#EDF7FB]">No customer — keep this task personal</button>
          {ENTITY_GROUPS.map(group => {
            const items = filtered.filter(o => o.group === group);
            if (items.length === 0) return null;
            return (
              <div key={group} className="mt-2">
                <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-[#78909C]">{group}</div>
                {items.map(option => {
                  const Icon = option.icon || UserRound;
                  return (
                    <button key={option.key} type="button" onClick={() => { onSelect(option.key); setOpen(false); setQuery(""); }}
                      data-testid={`customer-option-${option.key}`} className="w-full px-3 py-2 rounded-lg flex items-center gap-3 text-left hover:bg-[#EDF7FB] transition-colors">
                      <span className="w-8 h-8 rounded-lg bg-[#E4F4FA] text-[#07539A] flex items-center justify-center shrink-0"><Icon size={14} /></span>
                      <span className="min-w-0"><span className="block text-[13px] text-[#102A43] truncate">{option.label}</span><span className="block text-[10px] text-[#78909C] truncate">{option.detail}</span></span>
                    </button>
                  );
                })}
              </div>
            );
          })}
          {!loading && filtered.length === 0 && <div className="px-3 py-8 text-center text-[12px] text-[#78909C]">No matching records</div>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function TaskCard({ t, filter, onComplete, onUpdate }) {
  const [title, setTitle] = useState(t.title);
  const [dirty, setDirty] = useState(false);
  const isDone = filter === "completed";
  const src = SOURCE_META[t.source_type] || SOURCE_META.manual;
  const SrcIcon = src.icon;
  const linkHref = t.source_type === "order_action" ? "/orders"
    : t.opportunity_id ? `/opportunities/${t.opportunity_id}` : t.ticket_id ? "/tickets"
    : t.lead_id ? "/leads" : t.signal_id ? "/signals" : t.meeting_id ? "/meetings"
    : t.company_id ? "/universe" : null;
  const customerName = t.company?.name || t.opportunity?.name || t.ticket?.subject || "Personal task";
  const relatedName = t.company?.name && t.opportunity?.name ? t.opportunity.name : "";

  async function saveTitle() {
    if (!dirty || !title.trim()) return;
    await onUpdate({ title: title.trim() });
    setDirty(false);
    toast.success("Title updated");
  }

  async function bumpPriority() {
    const idx = PRIORITY_LIST.indexOf(t.priority);
    const next = PRIORITY_LIST[(idx + 1) % PRIORITY_LIST.length];
    await onUpdate({ priority: next });
    toast.success(`Priority → ${next}`);
  }

  async function setDue(days) {
    const due = new Date();
    due.setDate(due.getDate() + days);
    due.setHours(23, 59, 59, 999);
    await onUpdate({ due_date: due.toISOString() });
    toast.success(days === 0 ? "Due today" : `Due in ${days} day${days === 1 ? "" : "s"}`);
  }

  async function block() {
    await onUpdate({ status: "blocked", waiting_on: null });
    toast.info("Task blocked");
  }
  async function wait() {
    await onUpdate({ status: "deferred", waiting_on: "Awaiting follow-up" });
    toast.info("Task moved to Waiting");
  }
  async function resume() {
    await onUpdate({ status: "assigned", waiting_on: null, blocked_reason: null });
    toast.success("Task resumed");
  }

  return (
    <article className="group relative overflow-hidden rounded-[22px] border border-[#C9DCE6] bg-white shadow-[0_9px_25px_rgba(7,83,154,0.08)] hover:border-[#91C6DB] hover:shadow-[0_14px_34px_rgba(7,83,154,0.14)] transition-all" data-testid={`task-${t.id}`}>
      <div className="absolute left-0 top-5 bottom-5 w-2 rounded-r-full bg-[#1687EA]" />
      <div className="grid md:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 p-5 pl-7 md:p-6 md:pl-9">
          <div className="flex items-start gap-3">
            <button type="button" onClick={() => !isDone && onComplete()} data-testid={`complete-${t.id}`} title={isDone ? "Completed" : "Mark complete"}
              className={`mt-1 w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-all ${isDone ? "bg-[#059669] border-[#059669]" : "bg-white border-[#9FB8C4] hover:border-[#07539A] hover:bg-[#EDF7FB]"}`}>
              {isDone && <Check size={12} className="text-[#FFFFFF]" />}
            </button>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3 border-b border-black/10 focus-within:border-[#00A2D4] transition-colors">
                <input type="text" value={title} onChange={(e) => { setTitle(e.target.value); setDirty(true); }} onBlur={saveTitle}
                  onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} data-testid={`task-title-${t.id}`}
                  className={`w-full bg-transparent outline-none font-display text-lg text-[#101820] py-1.5 ${isDone ? "line-through text-[#78909C]" : ""}`} />
                {t.due_date && (
                  <span className="shrink-0 text-[9px] font-mono font-semibold text-[#07539A]">
                    Due {formatDate(t.due_date)}
                  </span>
                )}
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {linkHref ? <Link to={linkHref} data-testid={`task-link-${t.id}`} className="text-[14px] font-semibold text-[#07539A] hover:text-[#00A2D4] transition-colors">{customerName}</Link>
                  : <span className="text-[14px] font-semibold text-[#07539A]">{customerName}</span>}
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#07539A] text-[#FFFFFF] text-[10px] font-medium shadow-[0_4px_10px_rgba(7,83,154,0.18)]"><SrcIcon size={11} /> {src.label}</span>
              </div>
              {relatedName && <div className="mt-1 text-[11px] text-[#78909C] truncate">{relatedName}</div>}
              <div className="mt-3 flex items-center gap-3 text-[10px] text-[#78909C] flex-wrap">
                <button type="button" onClick={bumpPriority} data-testid={`priority-${t.id}`} title="Click to change priority" className="inline-flex items-center gap-1.5 capitalize hover:text-[#07539A]">
                  <span className="w-2 h-2 rounded-full" style={{ background: PRIORITY_COLOR[t.priority] }} />{t.priority || "medium"} priority
                </button>
                {t.ai_generated && <span className="inline-flex items-center gap-1 text-[#B26E1F]"><Sparkles size={11} /> AI generated</span>}
              </div>
            </div>
          </div>
        </div>

        <aside className="border-t md:border-t-0 md:border-l border-[#D9E7ED] bg-[#F8FCFE] p-4 md:p-5">
          <div className="text-[11px] font-semibold text-[#07539A]">Change due date</div>
          <div className="mt-2 flex items-center gap-1.5 flex-wrap">
            {!isDone && [0, 1, 3, 7].map(days => (
              <button key={days} type="button" onClick={() => setDue(days)} data-testid={`due-${days}-${t.id}`}
                className="h-7 px-2.5 rounded-lg bg-white border border-[#C8DCE6] text-[10px] text-[#244B5F] shadow-[0_3px_8px_rgba(7,83,154,0.07)] hover:border-[#00A2D4] hover:text-[#07539A] hover:-translate-y-0.5 transition-all">
                {days === 0 ? "Today" : `+${days}d`}
              </button>
            ))}
          </div>
          <div className="mt-4 text-[11px] font-semibold text-[#07539A]">Task actions</div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {isDone ? (
              <button type="button" onClick={resume} data-testid={`reopen-${t.id}`} className={TASK_ACTION_CLASS}><RotateCcw size={12} /> Reopen</button>
            ) : t.status === "blocked" || filter === "blocked" || filter === "waiting" ? (
              <button type="button" onClick={resume} data-testid={`resume-${t.id}`} className={TASK_ACTION_CLASS}><RotateCcw size={12} /> Resume</button>
            ) : (
              <><button type="button" onClick={block} data-testid={`block-${t.id}`} className={TASK_ACTION_CLASS}><Ban size={12} /> Block</button>
                <button type="button" onClick={wait} data-testid={`wait-${t.id}`} className={TASK_ACTION_CLASS}><Clock3 size={12} /> Waiting</button></>
            )}
          </div>
        </aside>
      </div>
    </article>
  );
}
